import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import styles from './Button.module.scss'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'soft' | 'danger' | 'dark'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  block?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  /** Render as a router link instead of a button. */
  to?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    block,
    leftIcon,
    rightIcon,
    to,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = clsx(styles.button, styles[variant], styles[size], block && styles.block, !children && styles.iconOnly, className)
  const content = (
    <>
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </>
  )
  if (to) {
    return (
      <Link to={to} className={classes} aria-disabled={disabled || loading ? true : undefined}>
        {content}
      </Link>
    )
  }
  return (
    <button ref={ref} type={type} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {content}
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'leftIcon' | 'rightIcon'> {
  label: string
  children: ReactNode
}

/** Square button with only an icon; `label` becomes the accessible name and tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', children, ...rest },
  ref,
) {
  return (
    <Button ref={ref} variant={variant} aria-label={label} title={label} {...rest}>
      {null}
      {children}
    </Button>
  )
})
